// Pick a classroom the way a big campus needs it: narrow by department, then
// programme, or just type a name / ID — any of the three, in any order. The
// list is searched and paged on the server (getClassRooms), so it works the
// same for 20 classrooms or 20,000.
//
//   <ClassroomPicker value={classroom} onChange={setClassroom} />
//
// The field shows the choice; clicking it opens a large drop-down panel (a
// popover, not a second dialog: inside a dialog a nested dialog makes the
// outer one close as focus moves into it).
import { useEffect, useRef, useState } from "react";
import { ChevronsUpDown, Loader2, School, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DataPager } from "@/components/DataPager";
import { SearchableSelect } from "@/components/SearchableSelect";
import { environment } from "@/env/env.prod";
import { apiHeaders } from "@/Auth/api-headers.";
import { useInstituteLabels } from "@/lib/instituteLabels";
import { useStructure } from "./ClassroomStructure";

export type PickedClassroom = { id: string; name: string; department_name?: string | null; programme_id?: string | null };

const PAGE_SIZE = 20;

type Props = {
    value: PickedClassroom | null;
    onChange: (classroom: PickedClassroom) => void;
    placeholder?: string;
};

export function ClassroomPicker({ value, onChange, placeholder }: Props) {
    const L = useInstituteLabels();
    const structure = useStructure();
    const [ open, setOpen ] = useState(false);
    const [ department, setDepartment ] = useState<string | null>(null);
    const [ programme, setProgramme ] = useState<string | null>(null);
    const [ query, setQuery ] = useState("");
    const [ page, setPage ] = useState(1);
    const [ rows, setRows ] = useState<PickedClassroom[]>([]);
    const [ total, setTotal ] = useState(0);
    const [ loading, setLoading ] = useState(false);
    const seq = useRef(0);

    const programmes = department ? structure.programmes.filter(p => p.departmentName === department) : [];

    useEffect(() => { setPage(1); }, [ department, programme, query ]);
    useEffect(() => {
        if (!open) return;
        const mine = ++seq.current;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const params = new URLSearchParams({
                    query: query.trim(), page: String(page), limit: String(PAGE_SIZE),
                    ...(department ? { departmentName: department } : {}),
                    ...(programme ? { programmeId: programme } : {}),
                });
                const res = await fetch(`${environment.BASE_URL}/classrooms/getClassRooms?${params}`, { headers: apiHeaders() as HeadersInit });
                const json = await res.json();
                if (mine !== seq.current) return;
                setRows(json.success ? (json.classrooms || []) : []);
                setTotal(json.success ? Number(json.total) || 0 : 0);
            } catch {
                if (mine === seq.current) { setRows([]); setTotal(0); }
            } finally {
                if (mine === seq.current) setLoading(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [ open, department, programme, query, page ]);

    const pick = (c: PickedClassroom) => {
        onChange({ id: c.id, name: c.name, department_name: c.department_name ?? null, programme_id: c.programme_id ?? null });
        setOpen(false);
    };

    const where = (c: PickedClassroom) =>
        [ c.department_name ? structure.departmentLabel(c.department_name) : null, structure.programmeLabel(c.programme_id ?? null) || null ]
            .filter(Boolean).join(" · ");

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
                    <span className="truncate">{value ? `${value.name} (${value.id})` : (placeholder ?? `Choose a ${L.classroom.toLowerCase()}…`)}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="flex max-h-[70vh] w-[min(720px,92vw)] flex-col gap-0 p-0">
                <div className="space-y-2 border-b p-3">
                    <p className="flex items-center gap-2 text-sm font-semibold">
                        <School className="h-4 w-4 text-primary" /> Choose a {L.classroom.toLowerCase()}
                        <span className="font-normal text-muted-foreground">— narrow by {L.department.toLowerCase()} and {L.programme.toLowerCase()}, or search</span>
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                        {structure.available !== false && structure.departments.length > 0 && (
                            <SearchableSelect
                                value={department}
                                nullLabel={`All ${L.departments.toLowerCase()}`}
                                searchPlaceholder={`Search ${L.departments.toLowerCase()}…`}
                                options={structure.departments.map(d => ({ value: d.departmentName, label: structure.departmentLabel(d.departmentName) }))}
                                onChange={(v) => { setDepartment(v); setProgramme(null); }}
                            />
                        )}
                        {programmes.length > 0 && (
                            <SearchableSelect
                                value={programme}
                                nullLabel={`All ${L.programmes.toLowerCase()}`}
                                searchPlaceholder={`Search ${L.programmes.toLowerCase()}…`}
                                options={programmes.map(p => ({ value: p.id, label: p.name }))}
                                onChange={setProgramme}
                            />
                        )}
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input autoFocus className="h-9 pl-9" placeholder={`Search ${L.classrooms.toLowerCase()} by name or ID…`}
                            value={query} onChange={(e) => setQuery(e.target.value)} />
                        {query && (
                            <button type="button" aria-label="Clear search" onClick={() => setQuery("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
                        )}
                    </div>
                </div>

                <div className={`min-h-0 flex-1 overflow-y-auto ${loading && rows.length > 0 ? "opacity-60" : ""}`}>
                    {loading && rows.length === 0 ? (
                        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
                    ) : rows.length === 0 ? (
                        <p className="py-10 text-center text-sm text-muted-foreground">No {L.classrooms.toLowerCase()} match.</p>
                    ) : (
                        <Table>
                            <TableHeader className="sticky top-0 bg-popover">
                                <TableRow className="bg-muted/30">
                                    <TableHead className="pl-4">{L.classroom}</TableHead>
                                    <TableHead>{L.department} / {L.programme}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map(c => (
                                    <TableRow key={c.id} className={`cursor-pointer ${value?.id === c.id ? "bg-primary/10" : ""}`} onClick={() => pick(c)}>
                                        <TableCell className="pl-4">
                                            <div className="font-medium">{c.name}</div>
                                            <div className="font-mono text-[11px] text-muted-foreground">{c.id}</div>
                                        </TableCell>
                                        <TableCell className="text-sm text-muted-foreground">{where(c) || "—"}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
                <DataPager className="border-t px-3 py-2" page={page} pageSize={PAGE_SIZE} total={total} onPage={setPage} loading={loading} />
            </PopoverContent>
        </Popover>
    );
}
